import React from "react";
import { Calendar, Clock, User, Tag, Share2, ArrowRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="25561" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-96 w-full animate-pulse rounded-none bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group relative flex h-full flex-col overflow-hidden border-border border-t-0 bg-card shadow-sm transition-all hover:shadow-lg hover:shadow-primary/5 rounded-none">
          <div className="h-[2px] w-full bg-gradient-to-r from-primary to-chart-4" />
          {post.featuredImage && (
            <div className="relative aspect-video w-full overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="p-6 pb-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-sans font-semibold text-primary uppercase tracking-widest">
              {post.categories?.[0]?.name || "Lead Gen Insights"}
            </div>
            <CardTitle className="font-serif text-xl leading-tight text-foreground group-hover:text-primary transition-colors">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow px-6 py-0">
            <div
              className="line-clamp-3 text-sm text-muted-foreground font-sans leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="p-6 pt-4">
            <Link to={post.link || "#"} className="flex items-center text-sm font-semibold text-primary hover:underline">
              Read Article <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection() {
  
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <div className="mt-24 border-t border-border pt-16">
      <div className="mb-10 flex items-end justify-between">
        <div>
          <h2 className="font-serif text-3xl font-bold text-foreground">Recommended for You</h2>
          <p className="mt-2 text-muted-foreground font-sans">Strategies and trends in high-conversion lead generation for home improvement.</p>
        </div>
        <Link to="/OurExpertisePage">
          <Button variant="outline" className="hidden sm:flex rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            View All Insights
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={{ per_page: 3, orderby: "date", order: "desc" }}>
        <RelatedPostsConsumer />
      </PostsProvider>
      <div className="mt-8 flex justify-center sm:hidden">
        <Link to="/OurExpertisePage">
          <Button variant="outline" className="w-full rounded-none border-primary text-primary">
            View All Insights
          </Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 px-6 py-12">
        <div className="h-8 w-24 animate-pulse rounded-none bg-muted" />
        <div className="h-12 w-3/4 animate-pulse rounded-none bg-muted" />
        <div className="aspect-video w-full animate-pulse rounded-none bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded-none bg-muted" />
          <div className="h-4 w-full animate-pulse rounded-none bg-muted" />
          <div className="h-4 w-5/6 animate-pulse rounded-none bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-center">
        <div className="max-w-md px-6">
          <h2 className="text-2xl font-bold text-foreground font-serif">Error Loading Article</h2>
          <p className="mt-2 text-muted-foreground font-sans">We encountered an issue while retrieving this content.</p>
          <Link to="/HomePage">
            <Button className="mt-6 rounded-none bg-[var(--gradient-primary)] text-primary-foreground hover:shadow-primary/20 transition-all">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-center">
        <div className="max-w-md px-6">
          <h2 className="text-2xl font-bold text-foreground font-serif">Article Not Found</h2>
          <p className="mt-2 text-muted-foreground font-sans">The insight you are looking for has been moved or no longer exists.</p>
          <Link to="/HomePage">
            <Button className="mt-6 rounded-none bg-[var(--gradient-primary)] text-primary-foreground hover:shadow-primary/20 transition-all">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Axebox Media Team";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const date = post.date ? new Date(post.date).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <article className="animate-in fade-in slide-in-from-bottom-4 duration-700">
      {}
      <header className="mx-auto max-w-4xl px-6 pt-12 pb-8 text-center md:pt-20 md:pb-12">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
          {post.categories?.map((cat, i) => (
            <span key={i} data-index={i} className="rounded-none bg-primary/10 px-3 py-1 text-xs font-semibold text-primary uppercase tracking-widest">
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl tracking-tight">
          {post.title}
        </h1>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground font-sans">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 border border-border rounded-none">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="rounded-none"><User className="h-4 w-4" /></AvatarFallback>
            </Avatar>
            <span className="font-semibold text-foreground">{authorName}</span>
          </div>
          {date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{date}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>Read Time: 5 min.</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mx-auto max-w-6xl px-6">
          <div className="relative aspect-[21/9] w-full overflow-hidden rounded-none shadow-lg">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      )}

      {}
      <div className="mx-auto max-w-3xl px-6 py-12 md:py-16">
        <div 
          className="prose prose-lg prose-zinc max-w-none 
            prose-headings:font-serif prose-headings:font-bold prose-headings:text-foreground 
            prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:font-sans
            prose-a:text-primary prose-a:no-underline prose-a:transition-colors hover:prose-a:text-primary/80
            prose-strong:font-bold prose-strong:text-foreground
            prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:py-2 prose-blockquote:pl-6 prose-blockquote:italic
            prose-img:rounded-none prose-img:shadow-md"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        <div className="mt-12 flex flex-col gap-6 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {post.tags?.map((tag, i) => (
              <div key={i} data-index={i} className="flex items-center gap-1 rounded-none bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                <Tag className="h-3 w-3" />
                {tag.name}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-foreground font-sans">Share:</span>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-none text-muted-foreground hover:text-primary hover:border-primary/30">
              <Share2 className="h-4 w-4" />
            </Button>
            {}
          </div>
        </div>

        {}
        <div className="relative mt-12 overflow-hidden rounded-none border border-border border-t-0 bg-card p-8 shadow-sm">
          <div className="absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r from-primary to-chart-4" />
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <Avatar className="h-20 w-20 border-2 border-background shadow-sm rounded-none">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-primary/10 text-primary rounded-none"><User className="h-8 w-8" /></AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <h3 className="font-serif text-xl font-bold text-foreground">About {authorName}</h3>
              <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                Lead generation strategist at Axebox Media. Specializing in high-intent campaigns for home improvement sectors, ensuring quality connections between brands and homeowners.
              </p>
              <Link to="/AxeboxEdgePage" className="inline-flex items-center text-sm font-semibold text-primary hover:underline">
                Learn More <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="bg-muted/30 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <RelatedPostsSection />
        </div>
      </div>
    </article>
  );
}



export default function SinglePostTemplate() {
  
  const currentParams = (typeof window !== 'undefined' && (window as any).wvcClient?.getCurrentWPQueryParams()) 
    || {};
    
  const wp_query = new WP_Query(currentParams);

  return (
    <section id="single-post-template-section" className="min-h-screen bg-background font-sans text-foreground">
      {}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Link to="/OurExpertisePage" className="flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary">
            <ChevronLeft className="h-4 w-4" />
            Back to Insights
          </Link>
          <Link to="/HomePage">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586938/img/axebox_media_logo.png" alt="Axebox Media" className="h-7" />
          </Link>
        </div>
      </div>

      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}